import { Fragment, useMemo, useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface DocumentRow {
  cite_key: string;
  title?: string | null;
  date?: string | null;
  url?: string | null;
  organization?: string | null;
  tags?: string[] | null;
  mentions?: number | null;        // per-doc mention count, when known
  snippet?: string | null;         // pre-highlighted (or raw) excerpt
  section_anchor?: string | null;
}

type SortKey = 'date' | 'title' | 'org' | 'mentions';
type SortDir = 'asc' | 'desc';

interface FacetChip {
  key: string;
  label: string;
  remove: () => void;
}

interface Props {
  documents: DocumentRow[];
  emptyLabel?: string;
  pageSize?: number;
  pivotLabel?: string;       // e.g. "Pivot to actor"
  onPivot?: (doc: DocumentRow) => void;
}

const PAGE_SIZE_DEFAULT = 30;

function getYear(date?: string | null): string | null {
  if (!date) return null;
  const m = /^(\d{4})/.exec(date);
  return m ? m[1] : null;
}

function csvEscape(v: unknown): string {
  const s = v == null ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function DocumentsTable({
  documents,
  emptyLabel = 'No documents.',
  pageSize = PAGE_SIZE_DEFAULT,
  pivotLabel,
  onPivot,
}: Props) {
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState<string | null>(null);
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(0);

  const years = useMemo(() => {
    const set = new Set<string>();
    for (const d of documents) {
      const y = getYear(d.date);
      if (y) set.add(y);
    }
    return Array.from(set).sort().reverse();
  }, [documents]);

  const orgs = useMemo(() => {
    const set = new Set<string>();
    for (const d of documents) {
      if (d.organization) set.add(d.organization);
    }
    return Array.from(set).sort();
  }, [documents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (yearFilter && getYear(d.date) !== yearFilter) return false;
      if (orgFilter && d.organization !== orgFilter) return false;
      if (q) {
        const blob = `${d.title || ''} ${d.organization || ''} ${d.snippet || ''} ${d.cite_key}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [documents, search, yearFilter, orgFilter]);

  const sorted = useMemo(() => {
    const copy = filtered.slice();
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'date': av = a.date || ''; bv = b.date || ''; break;
        case 'title': av = (a.title || a.cite_key).toLowerCase(); bv = (b.title || b.cite_key).toLowerCase(); break;
        case 'org': av = (a.organization || '').toLowerCase(); bv = (b.organization || '').toLowerCase(); break;
        case 'mentions': av = a.mentions ?? 0; bv = b.mentions ?? 0; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = useCallback((key: SortKey) => {
    setSortDir((curDir) => (sortKey === key ? (curDir === 'asc' ? 'desc' : 'asc') : 'desc'));
    setSortKey((cur) => (cur === key ? cur : key));
  }, [sortKey]);

  const toggleExpand = useCallback((key: string) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleExport = useCallback(() => {
    const rows = [['date', 'title', 'organization', 'cite_key', 'url', 'mentions']];
    for (const d of sorted) {
      rows.push([d.date || '', d.title || '', d.organization || '', d.cite_key, d.url || '', String(d.mentions ?? '')]);
    }
    const csv = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'documents.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [sorted]);

  const chips: FacetChip[] = [];
  if (yearFilter) chips.push({ key: 'year', label: `Year: ${yearFilter}`, remove: () => setYearFilter(null) });
  if (orgFilter) chips.push({ key: 'org', label: `Org: ${orgFilter}`, remove: () => setOrgFilter(null) });

  if (documents.length === 0) {
    return <div className="entity-table-empty">{emptyLabel}</div>;
  }

  const sortIndicator = (key: SortKey): ReactNode =>
    sortKey === key ? <span className="entity-table-sort">{sortDir === 'asc' ? '▲' : '▼'}</span> : null;

  return (
    <div className="entity-table">
      <div className="entity-table-toolbar">
        <input
          type="text"
          placeholder="Search title, org, snippet…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="entity-table-search"
        />
        {years.length > 0 && (
          <select
            value={yearFilter || ''}
            onChange={(e) => { setYearFilter(e.target.value || null); setPage(0); }}
            className="entity-table-facet"
          >
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {orgs.length > 0 && (
          <select
            value={orgFilter || ''}
            onChange={(e) => { setOrgFilter(e.target.value || null); setPage(0); }}
            className="entity-table-facet"
          >
            <option value="">All orgs</option>
            {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        <button onClick={handleExport} className="entity-table-export">CSV</button>
      </div>

      {chips.length > 0 && (
        <div className="entity-table-chips">
          {chips.map((c) => (
            <button key={c.key} className="entity-table-chip" onClick={c.remove}>
              {c.label} ×
            </button>
          ))}
        </div>
      )}

      <table className="entity-docs-table">
        <thead>
          <tr>
            <th className="entity-docs-col-toggle" />
            <th className="entity-docs-col-date" onClick={() => handleSort('date')}>Date {sortIndicator('date')}</th>
            <th onClick={() => handleSort('title')}>Report {sortIndicator('title')}</th>
            <th className="entity-docs-col-org" onClick={() => handleSort('org')}>Organization {sortIndicator('org')}</th>
            <th className="entity-docs-col-mentions" onClick={() => handleSort('mentions')}>n {sortIndicator('mentions')}</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((doc) => {
            const isOpen = expanded.has(doc.cite_key);
            return (
              <Fragment key={doc.cite_key}>
                <tr className={isOpen ? 'is-expanded' : ''}>
                  <td className="entity-docs-col-toggle">
                    <button
                      className="entity-docs-toggle"
                      onClick={() => toggleExpand(doc.cite_key)}
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >{isOpen ? '▾' : '▸'}</button>
                  </td>
                  <td className="entity-docs-col-date">{doc.date || ''}</td>
                  <td>
                    <Link to={`/sources/${encodeURIComponent(doc.cite_key)}`} className="ioc-detail-doc-title">
                      {doc.title || doc.cite_key}
                    </Link>
                    {doc.tags && doc.tags.length > 0 && (
                      <span className="entity-docs-tags">
                        {doc.tags.slice(0, 3).map((t) => (
                          <span key={t} className="entity-docs-tag">{t}</span>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="entity-docs-col-org">{doc.organization || ''}</td>
                  <td className="entity-docs-col-mentions">{doc.mentions ?? ''}</td>
                </tr>
                {isOpen && (
                  <tr className="entity-docs-expand-row">
                    <td />
                    <td colSpan={4}>
                      {doc.snippet ? (
                        <div className="entity-docs-snippet" dangerouslySetInnerHTML={{ __html: doc.snippet }} />
                      ) : (
                        <div className="entity-docs-snippet entity-docs-snippet-empty">No snippet available.</div>
                      )}
                      <div className="entity-docs-actions">
                        <Link to={`/sources/${encodeURIComponent(doc.cite_key)}${doc.section_anchor ? `#${doc.section_anchor}` : ''}`} className="ioc-detail-action-btn">Open report</Link>
                        {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" className="ioc-detail-action-btn">View source</a>}
                        {pivotLabel && onPivot && (
                          <button className="ioc-detail-action-btn" onClick={() => onPivot(doc)}>{pivotLabel}</button>
                        )}
                        <span className="entity-docs-cite">{doc.cite_key}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="entity-table-footer">
        <span>{sorted.length} result{sorted.length !== 1 ? 's' : ''}</span>
        {totalPages > 1 && (
          <span className="entity-table-pagination">
            <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹</button>
            <span>{page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>›</button>
          </span>
        )}
      </div>
    </div>
  );
}
